import React, { useRef, useState } from 'react';
import { Popover } from 'components/Shared/Popover';
import classNames from 'classnames';
import TriangleArrow from './TriangleArrow';

interface OptionType {
  value: string;
  label: string;
}

interface DropdownProps {
  name?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<{ name?: string; value: string }>) => void;
  options: OptionType[];
  placeholder?: string;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  className?: string;
  [key: string]: any;
}

function Dropdown({
  name,
  value,
  onChange,
  options = [],
  placeholder,
  onBlur,
  className,
  ...props
}: DropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selected = options.find((opt) => String(opt.value) === String(value));

  function handleSelect(selectedValue: string): void {
    if (onChange) {
      const event = {
        target: { name, value: selectedValue },
      } as React.ChangeEvent<{ name?: string; value: string }>;
      onChange(event);
    }
    setOpen(false);
  }

  function dropdownClickHandler(e: React.MouseEvent<HTMLDivElement>): void {
    e.preventDefault();
    setOpen((o) => !o);
  }

  function dropdownBlurHandler(e: React.FocusEvent<HTMLButtonElement, Element>): void {
    setOpen(false);
    onBlur?.(e);
  }

  return (
    <div className="dropdown-container" onClick={dropdownClickHandler}>
      <button
        className={classNames('dropdown-button', className)}
        ref={buttonRef}
        onBlur={dropdownBlurHandler}
        tabIndex={0}
        name={name}
        {...props}
      >
        <span>{selected ? selected.label : placeholder || 'Select...'}</span>
        <TriangleArrow direction={open ? 'up' : 'down'} />
      </button>
      <Popover anchorEl={buttonRef.current} open={open} onClose={(): void => setOpen(false)}>
        <ul className="dropdown-list">
          {options.map((opt) => (
            <li
              className="dropdown-item"
              key={opt.value}
              onMouseDown={(): void => handleSelect(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      </Popover>
      <style jsx>{`
        .dropdown-container {
          position: relative;
          width: 100%;
        }
        .dropdown-button {
          width: 100%;
          padding: 4px 0 5px 0;
          margin-bottom: 0;
          font-size: 20px;
          line-height: normal;
          font-weight: 400;
          border: none;
          border-bottom: 1px solid #000;
          border-radius: 0;
          background: transparent;
          transition: border-color 0.2s;
          box-sizing: border-box;
          outline: none;
          color: #000;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .dropdown-button:disabled {
          background: transparent;
          color: #d3d3d3;
        }
        .dropdown-list {
          position: absolute;
          left: 0;
          right: 0;
          z-index: 10;
          background: #fff;
          margin: 0;
          padding: 0;
          list-style: none;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          border-top: none;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px,
            rgba(0, 0, 0, 0.12) 0px 3px 14px 2px;
        }
        .dropdown-item {
          font-size: 18px;
          font-weight: 400;
          padding: 10px 12px;
          cursor: pointer;
        }
        .dropdown-item:hover {
          background: #d3d3d3;
        }
      `}</style>
    </div>
  );
}

export default Dropdown;
